import React, { type RefObject, useEffect, useRef } from 'react';

import type { PreloadedEntryPoint } from 'react-relay';

import type { ContentProps } from '@atlaskit/popup/types';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

import { WaitForEntryPointLoad } from '../wait-for-entry-point-load';

type FocusInsideOnLoadProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	/**
	 * Ref holding the popup's own initial focus setter, taken from the `content`
	 * render props. It is a ref because the content element is created before
	 * the render props are available.
	 */
	setInitialFocusRef: RefObject<ContentProps['setInitialFocusRef'] | null>;
};

function FocusInsideOnLoadInner({
	setInitialFocusRef,
}: Omit<FocusInsideOnLoadProps, 'entryPointReference'>): React.JSX.Element {
	const anchorRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		// `Popup` does not expose a ref to the element wrapping its content, so
		// it is resolved through this anchor, which renders as a sibling of the
		// loaded experience inside that element.
		const container = anchorRef.current?.parentElement;

		// Fence for if the container element doesn't exist, but this is
		// effectively not possible.
		if (!container) {
			return;
		}

		// Focus the first focusable element of the loaded experience, or the
		// popup itself when the experience has nothing focusable in it.
		const target = getFirstFocusable({ container }) ?? container;

		// The popup focuses its initial target once, when it opens. If that has
		// not happened yet — because the entry point was already loaded — this
		// makes the popup focus the target instead of itself, avoiding focus
		// moving twice.
		setInitialFocusRef.current?.(target);
		target.focus();
	}, [setInitialFocusRef]);

	return <span ref={anchorRef} hidden />;
}

/**
 * Moves focus into the popup once the entry point has finished loading, so
 * makers don't have to manage focus manually after the loading fallback is
 * replaced by the final experience.
 */
export function FocusInsideOnLoad({
	entryPointReference,
	setInitialFocusRef,
}: FocusInsideOnLoadProps): React.JSX.Element {
	return (
		<WaitForEntryPointLoad entryPointReference={entryPointReference}>
			<FocusInsideOnLoadInner setInitialFocusRef={setInitialFocusRef} />
		</WaitForEntryPointLoad>
	);
}
